
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { format } from "date-fns"
import { nb } from 'date-fns/locale';
import { useToast } from "@/components/ui/use-toast"
import { Badge } from "@/components/ui/badge"
import { Booking } from "@/types/booking"

interface BookingListProps {
  bookings: Booking[];
  isLoading: boolean;
  onDeleteBooking: (id: string) => void;
  onEditBooking: (booking: Booking) => void;
  onViewBooking: (booking: Booking) => void;
  filter: string;
}

const BookingList = ({ 
  bookings, 
  isLoading, 
  onDeleteBooking,
  onEditBooking,
  onViewBooking,
  filter
}: BookingListProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(new Date())

  const filteredBookings = filter === 'all' ? bookings : bookings.filter(booking => booking.status === filter);

  const getStatusBadge = (status: string) => {
    let className = "bg-gray-100 text-gray-800";
    
    if (status === "confirmed") {
      className = "bg-green-100 text-green-800";
    } else if (status === "pending") {
      className = "bg-yellow-100 text-yellow-800";
    } else if (status === "cancelled") {
      className = "bg-red-100 text-red-800";
    }
    
    const translatedStatus = t(`booking.statuses.${status}`, { defaultValue: status });
    
    return (
      <Badge className={className}>
        {translatedStatus}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto py-10">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">{t('common.date')}</TableHead>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('common.email')}</TableHead>
              <TableHead>{t('common.phone')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center">
                  {t('loading.bookings')}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && filteredBookings.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center">
                  {t('booking.noBookingsFound')}
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              filteredBookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell className="font-medium">{format(new Date(booking.startDate), "PPP", { locale: nb })}</TableCell>
                  <TableCell>{booking.contactName}</TableCell>
                  <TableCell>{booking.contactEmail}</TableCell>
                  <TableCell>{booking.contactPhone}</TableCell>
                  <TableCell>{getStatusBadge(booking.status)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="secondary" size="sm" onClick={() => onViewBooking(booking)}>
                      {t('common.view')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default BookingList;
